import { useEffect } from 'react';

import { CheckOutlined, Loading3QuartersOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Colors, SyncLine, Flex } from '@revenuemarket/cds';
import { colorTokens, semanticColors, thematicColors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast } from '@revenuemarket/ui';
import { Typography } from 'antd';

import { CompanyUserPermission } from '~apis/role/type';
import { postIntegratedScraping, SCRAPE_STATUS } from '~apis/scrape';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import checkIsOnboardingUser from '~hooks/checkIsOnboardingUser';
import useGetIntegratedScrapeStatus from '~hooks/queries/scraping/useGetIntegratedScrapeStatus';
import { useGetSettings } from '~hooks/queries/setting/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

export type Status = 'COMPLETE' | 'ERROR' | 'IN_PROGRESS' | 'REFRESH' | 'LABELING';

export const mapText = (status?: SCRAPE_STATUS) => {
  switch (status) {
    case 'IN_PROGRESS':
      return '수집 중';
    default:
      return '재수집';
  }
};

export const mapStatus = (status?: SCRAPE_STATUS) => {
  switch (status) {
    case 'IN_PROGRESS':
      return 'IN_PROGRESS';
    case 'LABELING':
      return 'LABELING';
    default:
      return 'REFRESH';
  }
};

const Icon = ({
  status,
  disabled,
}: {
  status: Status;
  disabled?: boolean;
  mode: 'light' | 'dark';
}) => {
  switch (status) {
    case 'COMPLETE':
      return (
        <CheckOutlined
          style={{
            color: disabled ? colorTokens.textDisabled : semanticColors.success.light,
          }}
        />
      );
    case 'ERROR':
      return (
        <WarningOutlined
          style={{
            color: disabled ? colorTokens.textDisabled : semanticColors.danger.regular,
          }}
        />
      );
    case 'REFRESH':
      return (
        <SyncLine
          fill={disabled ? colorTokens.textDisabled : Colors.gray700}
          width={16}
          height={16}
        />
      );
    case 'IN_PROGRESS':
      return (
        <Loading3QuartersOutlined
          spin
          style={{
            color: disabled ? colorTokens.textDisabled : thematicColors.primary.regular,
          }}
        />
      );
  }
};

const ScrapeBtn = () => {
  const { companyId } = useCompanyId();
  const settings = useGetSettings();

  const step = settings.data?.Data?.data?.onboardingStep;
  const isOnboardingUser = checkIsOnboardingUser(step);

  const { data: scrapeStatusData, refetch } = useGetIntegratedScrapeStatus(companyId as string);
  const scrapeStatus = scrapeStatusData?.Data?.data;

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        if (companyId) {
          refetch();
        }
      }
    }, 10000);
    return () => clearInterval(intervalId);
  }, [companyId]);

  const onScrape = async () => {
    if (!companyId) {
      return;
    }

    mixpanel.track(MIXPANEL_EVENT.SCRAPE.DEFAULT);
    const response = await postIntegratedScraping(companyId as string);

    if (response.Status === ResponseStatus.SUCCESS) {
      Toast.success('수집 요청이 완료되었습니다.');
      refetch();
    } else {
      Toast.error('수집 요청에 실패했습니다.');
    }
  };

  const disabled = isOnboardingUser || mapStatus(scrapeStatus?.overallStatus) === 'IN_PROGRESS';

  const status = mapStatus(scrapeStatus?.overallStatus);

  return (
    <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_DATA_INTEGRATION}>
      <Flex gap={8} align="center">
        <Button
          className={css`
            &&& .ant-btn-icon {
              margin-inline-end: 8px;
            }
          `}
          border
          disable={disabled}
          buttonSize="small"
          icon={<Icon status={status} disabled={disabled} mode={'light'} />}
          onClick={onScrape}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
          disabled={disabled}
        >
          <Typography.Text
            style={{
              fontSize: 14,
            }}
          >
            {mapText(scrapeStatus?.overallStatus)}
          </Typography.Text>
        </Button>
      </Flex>
    </PermissionGuard>
  );
};

export default ScrapeBtn;
